import { useRef, useState } from 'react'
import { useAsyncEffect } from 'utils'
import Align from '../../../types/align'

const PADDING = 6

function getFillStyle(context: CanvasRenderingContext2D, color: string): CanvasGradient | string {
    const colors = color.split(',')

    if (colors.length > 1) {
        const gradient = context.createLinearGradient(0, context.canvas.height, context.canvas.width, 0)

        colors.forEach((gradientColor, i) => {
            gradient.addColorStop(i / colors.length, gradientColor)
        })

        return gradient
    }

    return color
}

export function useText(
    text: string,
    width: number,
    color: string,
    fontSize: number,
    fontFamily: string,
    fontWeight: string,
    align: Align,
    strokeColor: string,
    strokeSize: number,
    scale: number = 1
): [HTMLCanvasElement, number] {
    const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
    const [lastDrawTime, setLastDrawTime] = useState<number>(0)

    useAsyncEffect(async () => {
        // preload font
        if (document && (document as any).fonts) {
            try {
                await (document as any).fonts.load(`${fontSize}px ${fontFamily}`)
            } catch (err) {}
        }

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')

        if (!context) return

        const lines = text.split('\n')
        const longestLine = lines.reduce((a, b) => (a.length > b.length ? a : b))

        context.font = `${fontWeight} ${fontSize}px ${fontFamily}`

        let measurements = context.measureText(longestLine)
        const textScale = Math.min(1, width / (measurements.width + strokeSize * 2)) // scale text down if too wide
        const scaledFontSize = Math.round(fontSize * textScale) * scale
        const scaledPadding = Math.round(PADDING * scale)
        const scaledStrokeSize = strokeSize * scale
        const scaledWidth = Math.round(width * scale)
        const lineHeight = scaledFontSize + scaledStrokeSize * 2

        context.font = `${fontWeight} ${scaledFontSize}px ${fontFamily}`
        measurements = context.measureText(longestLine)

        canvas.width = scaledWidth + scaledPadding * 2
        canvas.height = lineHeight * lines.length + scaledPadding * 2

        context.fillStyle = getFillStyle(context, color)
        context.font = `${fontWeight} ${scaledFontSize}px ${fontFamily}`
        context.lineCap = 'round'
        context.lineJoin = 'round'
        context.textAlign = 'left'
        context.textBaseline = 'top'

        if (strokeSize) {
            context.lineWidth = Math.round(strokeSize * 2) * scale
        }

        if (strokeColor) {
            context.strokeStyle = getFillStyle(context, strokeColor)
        }

        lines.forEach((line, i) => {
            let lineX = 0
            const lineY = i * lineHeight + scaledStrokeSize + scaledPadding
            const lineWidthDiff = Math.round(canvas.width - context.measureText(line).width)

            switch (align) {
                case 'center':
                    lineX = Math.round(lineWidthDiff / 2)
                    break
                case 'left':
                    lineX = scaledStrokeSize + scaledPadding
                    break
                case 'right':
                    lineX = lineWidthDiff - scaledStrokeSize - scaledPadding
                    break
            }

            if (strokeSize) {
                context.strokeText(line, lineX, lineY)
            }

            context.fillText(line, lineX, lineY)
        })

        setLastDrawTime(Date.now())
    }, [align, canvasRef, color, fontFamily, fontSize, fontWeight, scale, strokeColor, strokeSize, text, width])

    return [canvasRef.current, lastDrawTime]
}
